import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, formatDate } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge, DocTypeBadge } from '../../../../../lib/ui';
import { fetchDocumentos, fetchProcesos } from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import type {
  DocumentoResumen,
  Proceso,
  TipoDocumental,
  EstadoDocumento,
  PaginacionResponse,
} from '../../../../../types';

const TIPOS: TipoDocumental[] = ['PR', 'NC', 'IT', 'MN', 'PA', 'RE', 'FC', 'FL', 'HE', 'MO', 'PT'];
const ESTADOS: EstadoDocumento[] = [
  'BORRADOR',
  'EN_REVISION',
  'APROBADO',
  'PUBLICADO',
  'ARCHIVADO',
];

type SortField = 'codigo' | 'titulo' | 'tipoDocumental' | 'estado';
type SortDir = 'asc' | 'desc';

export function ExplorerPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [data, setData] = useState<PaginacionResponse<DocumentoResumen> | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroProceso, setFiltroProceso] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [sortField, setSortField] = useState<SortField>('codigo');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Fetch procesos once
  useEffect(() => {
    fetchProcesos()
      .then((r) => setProcesos(r.data))
      .catch(() => {});
  }, []);

  // Fetch documents
  const loadDocs = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = { pagina: String(pagina), porPagina: '20' };
    if (filtroTipo) params.tipoDocumental = filtroTipo;
    if (filtroEstado) params.estado = filtroEstado;
    if (filtroProceso) params.procesoId = filtroProceso;
    fetchDocumentos(params)
      .then((r) => setData(r))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pagina, filtroTipo, filtroEstado, filtroProceso]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const hasFilters = !!(filtroTipo || filtroEstado || filtroProceso || busqueda);

  // Client-side search + sort
  const displayDocs = useMemo(() => {
    if (!data) return [];
    let docs = [...data.datos];
    if (busqueda) {
      const q = busqueda.toLowerCase();
      docs = docs.filter(
        (d) => d.codigo.toLowerCase().includes(q) || d.titulo.toLowerCase().includes(q),
      );
    }
    docs.sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = String(av).localeCompare(String(bv), 'es', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return docs;
  }, [data, busqueda, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const sortIcon = (field: SortField) =>
    sortField !== field ? '' : sortDir === 'asc' ? ' \u25B2' : ' \u25BC';

  const pag = data?.paginacion;
  const from = pag ? (pag.pagina - 1) * pag.porPagina + 1 : 0;
  const to = pag ? Math.min(pag.pagina * pag.porPagina, pag.total) : 0;

  // Stats from current page data (total from pagination)
  const allDocs = data?.datos ?? [];
  const statsTotal = pag?.total ?? 0;
  const statsVigentes = allDocs.filter((d) => d.estado === 'PUBLICADO').length;
  const statsBorrador = allDocs.filter((d) => d.estado === 'BORRADOR').length;
  const statsRevision = allDocs.filter((d) => d.estado === 'EN_REVISION').length;

  const statCards = [
    {
      label: t.explorer.totalDocs,
      value: statsTotal,
      color: C.azul1,
      bg: '#fff',
      numBg: `${C.azul1}14`,
    },
    {
      label: t.explorer.published,
      value: statsVigentes,
      color: '#16A34A',
      bg: '#fff',
      numBg: '#F0FDF4',
    },
    {
      label: t.explorer.draft,
      value: statsBorrador,
      color: '#94A3B8',
      bg: '#fff',
      numBg: '#F1F5F9',
    },
    {
      label: t.explorer.inReview,
      value: statsRevision,
      color: '#F59E0B',
      bg: '#fff',
      numBg: '#FFFBEB',
    },
  ];

  const clearFilters = () => {
    setFiltroTipo('');
    setFiltroEstado('');
    setFiltroProceso('');
    setBusqueda('');
    setPagina(1);
  };

  const selectStyle: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 14,
    fontFamily: 'inherit',
    backgroundColor: C.surface,
    color: C.text,
    cursor: 'pointer',
    minWidth: 160,
  };

  return (
    <div>
      {/* Header con tooltip */}
      <div style={{ marginBottom: 14 }}>
        <h1
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1F3864',
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          {t.process.explorerTitle}
          <InfoTooltip
            title={t.process.tooltips.explorador.titulo}
            text={t.process.tooltips.explorador.ayuda}
          />
        </h1>
      </div>

      {/* Stats cards */}
      <div
        className="stats-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <style>{`
          @media (max-width: 900px) {
            .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (max-width: 500px) {
            .stats-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
        {statCards.map((card) => (
          <div
            key={card.label}
            style={{
              backgroundColor: C.surface,
              borderRadius: 10,
              padding: '14px 16px',
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                backgroundColor: card.numBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 800,
                color: card.color,
                flexShrink: 0,
              }}
            >
              {card.value}
            </div>
            <div style={{ fontSize: 12, color: C.textSec, fontWeight: 500 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filters row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <Ic d={ICON_PATHS.filter} s={16} c={C.textMut} />
        <select
          value={filtroTipo}
          onChange={(e) => {
            setFiltroTipo(e.target.value);
            setPagina(1);
          }}
          style={selectStyle}
        >
          <option value="">{t.explorer.allTypes}</option>
          {TIPOS.map((tp) => (
            <option key={tp} value={tp}>
              {tp} - {t.docType[tp]}
            </option>
          ))}
        </select>
        <select
          value={filtroEstado}
          onChange={(e) => {
            setFiltroEstado(e.target.value);
            setPagina(1);
          }}
          style={selectStyle}
        >
          <option value="">{t.explorer.allStates}</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {t.status[e]}
            </option>
          ))}
        </select>
        <select
          value={filtroProceso}
          onChange={(e) => {
            setFiltroProceso(e.target.value);
            setPagina(1);
          }}
          style={selectStyle}
        >
          <option value="">{t.explorer.allProcesses}</option>
          {procesos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.codigo} - {p.nombre}
            </option>
          ))}
        </select>
        <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={t.explorer.search}
            style={{
              ...selectStyle,
              width: '100%',
              paddingLeft: 34,
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <Ic d={ICON_PATHS.search} s={14} c={C.textMut} />
          </span>
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              backgroundColor: C.surface,
              color: C.textSec,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            <Ic d={ICON_PATHS.x} s={12} c={C.textSec} /> {t.explorer.clearFilters}
          </button>
        )}
      </div>

      {/* Count indicator */}
      {pag && (
        <div style={{ fontSize: 12, color: C.textMut, marginBottom: 8 }}>
          {t.explorer.showing(from, to, pag.total)}
        </div>
      )}

      {/* Table */}
      <div
        style={{
          backgroundColor: C.surface,
          borderRadius: 12,
          boxShadow: C.shadow1,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <LoadingSpinner text={t.docExplorer.cargandoDocumentos} size="sm" />
          </div>
        ) : displayDocs.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: C.textMut, fontSize: 13 }}>
            {t.docExplorer.noEncontrados}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="table-desktop" style={{ overflowX: 'auto' }}>
              <style>{`
                @media (max-width: 768px) {
                  .table-desktop { display: none !important; }
                  .cards-mobile { display: flex !important; }
                }
              `}</style>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {(
                      [
                        ['codigo', t.table.code],
                        ['titulo', t.table.title],
                        ['tipoDocumental', t.table.type],
                        [null, t.table.process],
                        ['estado', t.table.status],
                        [null, t.table.edition],
                        [null, t.table.date],
                      ] as [SortField | null, string][]
                    ).map(([field, label], i) => (
                      <th
                        key={i}
                        onClick={() => field && toggleSort(field)}
                        style={{
                          backgroundColor: C.azulMuyOscuro,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 12,
                          padding: '14px 20px',
                          textAlign: 'left',
                          cursor: field ? 'pointer' : 'default',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                          borderBottom: 'none',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                        }}
                      >
                        {label}
                        {field ? sortIcon(field) : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayDocs.map((doc, idx) => (
                    <tr
                      key={doc.id}
                      onClick={() => navigate(`/documento/${doc.codigo}`)}
                      style={{
                        backgroundColor: idx % 2 === 0 ? '#fff' : C.borderAlt,
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = `${C.azul2}14`;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          idx % 2 === 0 ? '#fff' : C.borderAlt;
                      }}
                    >
                      <td style={{ padding: '14px 20px' }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: C.azul1,
                            fontFamily: 'monospace',
                            fontSize: 12,
                          }}
                        >
                          {doc.codigo}
                        </span>
                      </td>
                      <td
                        style={{ padding: '14px 20px', fontSize: 14, color: C.text, maxWidth: 300 }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {doc.titulo}
                        </span>
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <DocTypeBadge type={doc.tipoDocumental} />
                      </td>
                      <td style={{ padding: '14px 20px', fontSize: 14, color: C.textSec }}>
                        {doc.proceso?.codigo ?? ''}
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <StatusBadge status={doc.estado} small />
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          fontSize: 14,
                          color: C.textSec,
                          textAlign: 'center',
                        }}
                      >
                        {String(doc.versionVigente?.numeroEdicion ?? 1).padStart(2, '0')}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          fontSize: 14,
                          color: C.textSec,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDate(doc.actualizadoEn)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div
              className="cards-mobile"
              style={{
                display: 'none',
                flexDirection: 'column',
                gap: 0,
              }}
            >
              {displayDocs.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => navigate(`/documento/${doc.codigo}`)}
                  style={{
                    padding: 14,
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.azul1,
                      }}
                    >
                      {doc.codigo}
                    </span>
                    <StatusBadge status={doc.estado} small />
                  </div>
                  <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}>{doc.titulo}</div>
                  <div style={{ display: 'flex', gap: 8, fontSize: 11, color: C.textMut }}>
                    <DocTypeBadge type={doc.tipoDocumental} />
                    <span>
                      Ed. {String(doc.versionVigente?.numeroEdicion ?? 1).padStart(2, '0')}
                    </span>
                    <span>{formatDate(doc.actualizadoEn)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pag && pag.totalPaginas > 1 && (
              <div
                style={{
                  padding: '12px 14px',
                  borderTop: `1px solid ${C.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ fontSize: 12, color: C.textMut }}>{pag.total} documentos</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => p - 1)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      backgroundColor: C.surface,
                      fontSize: 12,
                      color: C.textSec,
                      cursor: pagina <= 1 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      opacity: pagina <= 1 ? 0.4 : 1,
                    }}
                  >
                    {t.explorer.previous}
                  </button>
                  {Array.from({ length: Math.min(pag.totalPaginas, 7) }, (_, i) => i + 1).map(
                    (n) => (
                      <button
                        key={n}
                        onClick={() => setPagina(n)}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          border: 'none',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          backgroundColor: pagina === n ? C.azul2 : 'transparent',
                          color: pagina === n ? '#fff' : C.textSec,
                        }}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    disabled={pagina >= pag.totalPaginas}
                    onClick={() => setPagina((p) => p + 1)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      backgroundColor: C.surface,
                      fontSize: 12,
                      color: C.textSec,
                      cursor: pagina >= pag.totalPaginas ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      opacity: pagina >= pag.totalPaginas ? 0.4 : 1,
                    }}
                  >
                    {t.explorer.next}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
